import Promise from 'bluebird';
import { useCallback, useReducer, useRef } from 'react';

import { WindowStatus } from './asset-window';
import { AssetsWindowManager } from './assets-window-manager';
import { AssetsReducerActions } from './assets-reducer-base';

/*
  @reducer
  reducer function, support actions defined in AssetsReducerActions, and its state structure is transparent to this hook

  @reducerInitialArg
  initial state for reducer

  @repository
  repository object with following methods:
    - loadAssets: (...filters, ...params) => Promise<{ assets, totalItemCount }>
        filters : a filter object, which will be cached in this hook, and it's internal data is transparent to this hook
        params  : additional parameters for loading assets, which will be passed to loadAssets
                  it contains at least windowIndex and windowSize
      both filters and params will be passed to repository without any modification
 */
export const useAssetsServiceBase = ({
  reducer,
  reducerInitialArg,
  repository,
  pageSize,
  assetWindowManager,
}) => {
  const filtersRef = useRef(null);
  const loadingWindowManagerRef = useRef(assetWindowManager || new AssetsWindowManager(pageSize));

  const [assetState, dispatch] = useReducer(reducer, reducerInitialArg || null);

  const uploadImage = useCallback(({
    imageData,
    updateCurrentProgress = null,
    name = '',
    folderId = null,
    supportUpscale,
  }) => repository.uploadImageToFolder({
    imageData,
    updateCurrentProgress,
    imageName: name,
    folderId,
    supportUpscale,
  }), [repository]);

  /**
   * Load assets
   * @param {Object} filters
   * @param {Object} params
   * @returns {Promise}
   */
  const loadAssets = useCallback((filters, ...params) => {
    // reset loading status
    loadingWindowManagerRef.current.resetWindows();
    const winObj = loadingWindowManagerRef.current.getNextUnloadedWindow();

    // keep filter reference to use in loadMoreAssets
    filtersRef.current = filters;

    const ret = repository.loadAssets({
      ...filters,
      ...params,
      ...winObj,
    }).then(({ assets, totalItemCount, ...rest }) => {
      // update loading status
      loadingWindowManagerRef.current.setTotalItemCount(totalItemCount, winObj);
      loadingWindowManagerRef.current.setWindowStatus(winObj, WindowStatus.loaded);
      // save assets to reducer state
      dispatch(AssetsReducerActions.setAssetsData({ assets, ...rest }));
    }).catch((error) => {
      // upload loading status
      loadingWindowManagerRef.current.setWindowStatus(winObj, WindowStatus.error);
      throw error;
    });

    return ret;
  }, [repository]);

  /**
   * Load more assets
   * @param {Object} params
   * @returns {Promise}
   */
  const loadMoreAssets = useCallback((params) => {
    const winObj = loadingWindowManagerRef.current.getNextUnloadedWindow();

    // no more window to load
    if (winObj.windowIndex < 0) {
      return Promise.resolve({ noMoreData: true });
    }

    const ret = repository.loadAssets({
      ...filtersRef.current,
      ...params,
      ...winObj,
    }).then(({ assets, totalItemCount, ...rest }) => {
      loadingWindowManagerRef.current.setTotalItemCount(totalItemCount, winObj);
      loadingWindowManagerRef.current.setWindowStatus(winObj, WindowStatus.loaded);
      // save assets to reducer state
      dispatch(AssetsReducerActions.addAssetsData({
        assets, ...winObj, ...rest,
      }));
    }).catch((error) => {
      // upload loading status
      loadingWindowManagerRef.current.setWindowStatus(winObj, WindowStatus.error);
      throw error;
    });

    return ret;
  }, [repository]);

  /**
   * Reload assets
   * @param {Object} params
   * @returns {Promise}
   */
  const reloadAssets = useCallback((params) => {
    const ret = loadAssets(filtersRef.current, params);
    return ret;
  }, [loadAssets]);

  /**
   * Reset assets data
   */
  const resetAssetsData = useCallback(() => {
    dispatch(AssetsReducerActions.resetAssetData());
  }, []);

  /**
   * Rename asset
   */
  const renameAsset = useCallback(({
    assetId,
    folderAssetType,
    name,
  }) => {
    const ret = repository.renameAsset({
      assetId,
      folderAssetType,
      name,
    }).then(() => {
      dispatch(AssetsReducerActions.renameAssetData({
        assetId,
        name,
      }));
    });
    return ret;
  }, [repository]);

  return {
    ...assetState,
    uploadImage,
    loadAssets,
    loadMoreAssets,
    reloadAssets,
    resetAssetsData,
    renameAsset,
    //
    totalAssetCount: loadingWindowManagerRef.current.getTotalAssetCount(),
    isLoading: loadingWindowManagerRef.current.isLoading,
    isCompleted: loadingWindowManagerRef.current.isCompleted,

    // for debug/logging only
    // loadingWindowManagerRef,
    // filtersRef,
  };
};
